import apiService from './api';

export interface Setting {
  key: string;
  value: any;
  type: 'number' | 'string' | 'boolean' | 'object';
  label: string;
  description?: string;
  category: string;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SettingsResponse {
  status: string;
  results: number;
  data: Setting[];
}

export interface SettingResponse {
  status: string;
  data: Setting;
}

export interface DigipayConnectionTest {
  apiBase: string;
  host: string;
  configured: boolean;
  amountUnit?: string;
  clientIdHasNonAscii?: boolean;
  dns: { ok: boolean; addresses: string[]; error?: string };
  login: { ok: boolean; error?: string; ms: number };
  hint: string;
}

export interface SnappPayConnectionTest {
  apiBase: string;
  host: string;
  configured: boolean;
  dns: { ok: boolean; addresses: string[]; error?: string };
  login: { ok: boolean; error?: string; ms: number };
  eligible: { ok: boolean; eligible?: boolean; title?: string; description?: string; error?: string };
  hint: string;
}

class SettingService {
  private baseUrl = '/admin/settings';

  /**
   * Get all settings
   */
  public async getAllSettings(): Promise<Setting[]> {
    try {
      const response = await apiService.get<SettingsResponse>(this.baseUrl);
      
      // Handle different response formats
      if (response && 'status' in response && 'data' in response) {
        // New API format: { status: 'success', results: 4, data: [...] }
        return (response as SettingsResponse).data;
      } else if (Array.isArray(response)) {
        // Original format: direct array of settings
        return response;
      } else {
        console.error('Unexpected response format:', response);
        return [];
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      return []; // Return empty array instead of throwing
    }
  }

  /**
   * Get settings by category
   */
  public async getSettingsByCategory(category: string): Promise<Setting[]> {
    try {
      const response = await apiService.get<SettingsResponse>(`${this.baseUrl}/category/${category}`);
      
      // Handle different response formats
      if (response && 'status' in response && 'data' in response) {
        // New API format
        return (response as SettingsResponse).data;
      } else if (Array.isArray(response)) {
        // Original format
        return response;
      } else {
        console.error('Unexpected response format:', response);
        return [];
      }
    } catch (error) {
      console.error(`Error fetching settings for category ${category}:`, error);
      return []; // Return empty array instead of throwing
    }
  }

  /**
   * Get setting by key
   */
  public async getSetting(key: string): Promise<Setting | null> {
    try {
      const response = await apiService.get<SettingResponse>(`${this.baseUrl}/${key}`);
      
      // Handle different response formats
      if (response && 'status' in response && 'data' in response) {
        // New API format
        return (response as SettingResponse).data;
      } else {
        // Original format
        return response as unknown as Setting;
      }
    } catch (error) {
      console.error(`Error fetching setting with key ${key}:`, error);
      return null; // Return null instead of throwing
    }
  }

  /**
   * Update setting
   */
  public async updateSetting(key: string, value: any): Promise<Setting | null> {
    try {
      const response = await apiService.patch<SettingResponse>(`${this.baseUrl}/${key}`, { value });
      
      // Handle different response formats
      if (response && 'status' in response && 'data' in response) {
        // New API format
        return (response as SettingResponse).data;
      } else {
        // Original format
        return response as unknown as Setting;
      }
    } catch (error) {
      console.error(`Error updating setting ${key}:`, error);
      return null; // Return null instead of throwing
    }
  }

  /**
   * تست اتصال دیجی‌پی (DNS + لاگین OAuth) — بدون تراکنش
   */
  public async testDigipayConnection(): Promise<DigipayConnectionTest> {
    const response: any = await apiService.get(`${this.baseUrl}/digipay/test-connection`);
    return (response?.data?.data || response?.data || response) as DigipayConnectionTest;
  }

  /**
   * تست اتصال اسنپ‌پی (DNS + لاگین OAuth + استعلام eligible) — بدون تراکنش
   */
  public async testSnappPayConnection(): Promise<SnappPayConnectionTest> {
    const response: any = await apiService.get(`${this.baseUrl}/snapppay/test-connection`);
    return (response?.data?.data || response?.data || response) as SnappPayConnectionTest;
  }

  /**
   * Initialize system settings
   */
  public async initializeSettings(): Promise<boolean> {
    try {
      await apiService.post(`${this.baseUrl}/initialize`);
      return true;
    } catch (error) {
      console.error('Error initializing settings:', error);
      return false; // Return false instead of throwing
    }
  }
}

export default new SettingService(); 